import React from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import {
  usePrefersReducedMotion,
  vibrate,
  sanitizeBodyHtml,
  aspectBandFraction,
} from "./cardCommon";

const AUTO_ADVANCE_MS = 7000;

// Split-layout image band bounds. The band flexes toward the photo's aspect
// (via aspectBandFraction in cardCommon), clamped to [MIN, MAX] of card height
// so neither the photo nor the text band gets starved.
const SPLIT_BAND_MIN = 0.4;
const SPLIT_BAND_MAX = 0.6;
// Empty-state placeholder band height (no image yet). Slightly taller than
// the loaded floor so the empty split reads as a balanced "image on top".
const SPLIT_BAND_EMPTY = 0.45;

// Relative text-size steps. Each layout has its own baseline (medium) because
// the layouts are visually different — text_only is big typography, split is
// compact. Small/Large step one Tailwind size off the baseline. `medium`
// matches the previously-hardcoded sizes so existing cards are unchanged.
const HEADLINE_SIZE_CLASSES = {
  full_bleed: { small: "text-2xl", medium: "text-3xl", large: "text-4xl" },
  split: { small: "text-xl", medium: "text-2xl", large: "text-3xl" },
  text_only: { small: "text-3xl", medium: "text-4xl", large: "text-5xl" },
};
const BODY_SIZE_CLASSES = {
  full_bleed: { small: "text-sm", medium: "text-base", large: "text-lg" },
  split: { small: "text-sm", medium: "text-base", large: "text-lg" },
  text_only: { small: "text-base", medium: "text-lg", large: "text-xl" },
};

function headlineSizeClass(layoutKey, size) {
  const scale = HEADLINE_SIZE_CLASSES[layoutKey] || HEADLINE_SIZE_CLASSES.full_bleed;
  return scale[size] || scale.medium;
}
function bodySizeClass(layoutKey, size) {
  const scale = BODY_SIZE_CLASSES[layoutKey] || BODY_SIZE_CLASSES.full_bleed;
  return scale[size] || scale.medium;
}

/**
 * Non-interactive content card. Renders one of three layouts, all driven
 * off the same `{headline, body, emoji}` content schema:
 *
 *   - `full_bleed`  — image fills card, dark gradient overlay at bottom,
 *                     headline + body bottom-aligned. The TikTok-y default.
 *   - `split`       — image top half, white background bottom half with
 *                     readable text. Best for dense info.
 *   - `text_only`   — white background, big typography.
 *                     For punchy stats / quotes / "did you know?" cards.
 *
 * Auto-advances after 7s when active. Pause-on-press lets the user hold
 * to keep reading; release resumes a fresh 7s window (intentionally
 * coarse — admins won't reason about precise pause/resume semantics, and
 * "give me 7 more seconds" is the intuitive meaning).
 */
export function ImageTextCard({
  card,
  isActive,
  shouldLoadMedia = true,
  onAdvance,
}) {
  const reducedMotion = usePrefersReducedMotion();
  const layout = card.layout_variant || "full_bleed";
  const content = card.content || {};
  const headline = content.headline || "";
  const body = content.body || "";
  const emoji = content.emoji || null;
  // Layout-aware defaults — match the editor's defaults so saved cards
  // render the same in editor / tile / player.
  const textPosition =
    content.text_position || (layout === "text_only" ? "middle" : "bottom");
  const textAlign =
    content.text_align || (layout === "text_only" ? "center" : "left");
  const headlineSize = content.headline_size || "medium";
  const bodySize = content.body_size || "medium";
  // Headline is bold by default (undefined flag === bold), italic off — these
  // mirror the editor's defaults so undecorated/legacy cards look unchanged.
  const headlineWeightClass =
    content.headline_bold === false ? "font-normal" : "font-bold";
  const headlineItalicClass = content.headline_italic === true ? "italic" : "";
  const imageUrl = shouldLoadMedia ? card.media?.url || null : null;
  // Sanitize once per render rather than at every dangerouslySetInnerHTML
  // site. The body is Tiptap-emitted HTML, but the BE persists
  // content_json verbatim — sanitizing on render closes the stored-XSS
  // path even if a crafted API payload bypassed the editor.
  const safeBody = React.useMemo(() => sanitizeBodyHtml(body), [body]);
  const hasBodyContent = safeBody && safeBody !== "<p></p>";

  const textAlignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[textAlign];

  // Flex alignment helpers for text_only (which uses flex layout, not the
  // absolute positioning that full_bleed uses).
  const justifyClassForTextOnly = {
    top: "justify-start",
    middle: "justify-center",
    bottom: "justify-end",
  }[textPosition];
  const itemsClassForTextOnly = {
    left: "items-start",
    center: "items-center",
    right: "items-end",
  }[textAlign];

  const [isPaused, setIsPaused] = React.useState(false);

  // Split-layout image band height as a fraction of card height. Defaults to
  // the floor (split only ever receives landscape photos, which clamp there
  // anyway, so the post-load adjustment is small-to-none); refined once the
  // image's natural dimensions are known.
  const [splitBand, setSplitBand] = React.useState(SPLIT_BAND_MIN);
  // Until the photo decodes the band shows a skeleton instead of a black flash
  // + hard pop-in. We have no stored dimensions (the media payload is just
  // {uuid, url}), so the band can only ease to the photo's aspect on load.
  const [splitImgLoaded, setSplitImgLoaded] = React.useState(false);
  const splitImgRef = React.useRef(null);
  const handleSplitImageLoad = React.useCallback((e) => {
    const img = e.currentTarget;
    setSplitBand(
      aspectBandFraction(
        img.naturalWidth,
        img.naturalHeight,
        SPLIT_BAND_MIN,
        SPLIT_BAND_MAX,
      ),
    );
    setSplitImgLoaded(true);
  }, []);
  // Reset on src change, and catch already-cached images (whose onLoad may
  // never fire, which would otherwise leave the skeleton stuck forever).
  React.useEffect(() => {
    const img = splitImgRef.current;
    if (img && img.complete && img.naturalWidth) {
      setSplitBand(
      aspectBandFraction(
        img.naturalWidth,
        img.naturalHeight,
        SPLIT_BAND_MIN,
        SPLIT_BAND_MAX,
      ),
    );
      setSplitImgLoaded(true);
    } else {
      setSplitImgLoaded(false);
    }
  }, [imageUrl]);

  React.useEffect(() => {
    if (!isActive || isPaused) return undefined;
    const handle = setTimeout(() => {
      onAdvance && onAdvance();
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(handle);
  }, [isActive, isPaused, onAdvance]);

  // Light haptic when this card becomes active — same micro-acknowledgment
  // TikTok / Stories give as a card snaps into place.
  React.useEffect(() => {
    if (isActive) vibrate(8);
  }, [isActive]);

  const pressHandlers = {
    onMouseDown: () => setIsPaused(true),
    onMouseUp: () => setIsPaused(false),
    onMouseLeave: () => setIsPaused(false),
    onTouchStart: () => setIsPaused(true),
    onTouchEnd: () => setIsPaused(false),
    onTouchCancel: () => setIsPaused(false),
  };

  const fadeVariants = {
    initial: { opacity: 0 },
    enter: { opacity: 1, transition: { duration: reducedMotion ? 0 : 0.35 } },
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Read-more (TikTok-style). The card shows what fits and clips the rest
  // behind a "… more" tap that opens the full text in a modal sheet. The
  // sheet is portaled to <body>, so it's outside the deck's Embla viewport
  // and its scroll/touch never collides with the swipe gesture. The button
  // only appears when the body actually overflows the card, so cards that
  // already fit are unchanged.
  const bodyRef = React.useRef(null);
  const [overflowing, setOverflowing] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const measureOverflow = React.useCallback(() => {
    const el = bodyRef.current;
    if (el) setOverflowing(el.scrollHeight - el.clientHeight > 4);
  }, []);
  React.useLayoutEffect(() => {
    measureOverflow();
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measureOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureOverflow, body, headline, layout, imageUrl, splitBand]);
  // Collapse the sheet when this card is no longer the active one.
  React.useEffect(() => {
    if (!isActive) setExpanded(false);
  }, [isActive]);
  const openExpand = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded(true);
  };
  // Focus the sheet's scroll region when it opens so Arrow keys scroll it
  // (the player suppresses its own deck hotkeys while the sheet is open —
  // see the `data-deck-sheet` check in SwipeDeckPlayer's key handler).
  const sheetScrollRef = React.useRef(null);
  React.useEffect(() => {
    if (expanded && sheetScrollRef.current) {
      sheetScrollRef.current.focus({ preventScroll: true });
    }
  }, [expanded]);

  // The "… more" affordance: a bottom fade over the clipped text with a
  // tappable label. stopPropagation on press so it never starts a swipe.
  const moreButton = overflowing ? (
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={openExpand}
      className="absolute bottom-0 left-0 right-0 flex justify-start px-6 pt-10 pb-3 bg-gradient-to-t from-white via-white to-transparent"
    >
      <span className="text-sm font-semibold text-violet-700">… more</span>
    </button>
  ) : null;

  // The full-text sheet. Portaled into the phone-frame element (the swipe
  // area's parent, not inside it) so the deck's Embla gesture never sees
  // it, while staying confined to the phone preview. Always a bottom sheet
  // that rises from the bottom of the frame, since the deck is rendered in
  // the phone frame on desktop too. Falls back to <body> if the frame
  // isn't found.
  const sheetTarget =
    typeof document !== "undefined"
      ? document.getElementById("swipe-deck-frame") || document.body
      : null;
  const expandSheet =
    expanded && sheetTarget
      ? createPortal(
          <div
            data-deck-sheet="open"
            className="absolute inset-0 z-30 flex items-end justify-center"
            onClick={() => setExpanded(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setExpanded(false);
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
            <div
              className="relative flex flex-col w-full max-h-[85%] bg-white rounded-t-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 leading-snug">
                  {headline}
                </h3>
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  aria-label="Close"
                  className="shrink-0 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center"
                >
                  <CloseIcon style={{ fontSize: 18 }} />
                </button>
              </div>
              <div
                ref={sheetScrollRef}
                tabIndex={0}
                className="overflow-y-auto px-6 py-4 text-base text-gray-700 leading-relaxed break-words focus:outline-none [&_p]:my-0 [&_p+p]:mt-3 [&_p:empty]:h-[1.5em] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-0 [&_li]:my-0 [&_li>p]:my-0"
                style={{ touchAction: "pan-y" }}
                dangerouslySetInnerHTML={{ __html: safeBody }}
              />
            </div>
          </div>,
          sheetTarget,
        )
      : null;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Text-only layout. Also the fallback for full_bleed with no image yet —
  // but NOT split: split keeps its structure and shows an image placeholder
  // (handled in the split branch below) so selecting "Split" immediately
  // reads as a split card awaiting a photo.

  if (layout === "text_only" || (!imageUrl && layout !== "split")) {
    return (
      <div
        {...pressHandlers}
        className="relative w-full h-full bg-white select-none"
      >
        <div
          ref={bodyRef}
          className={`absolute inset-0 overflow-hidden flex flex-col px-8 py-14 ${
            overflowing ? "justify-start" : justifyClassForTextOnly
          } ${itemsClassForTextOnly}`}
        >
          <motion.div
            variants={fadeVariants}
            initial="initial"
            animate="enter"
            className={`flex flex-col w-full max-w-md ${textAlignClass}`}
          >
            {emoji && <div className="text-6xl mb-6">{emoji}</div>}
            <h2
              className={`${headlineSizeClass("text_only", headlineSize)} ${headlineWeightClass} ${headlineItalicClass} mb-4 leading-tight break-words text-gray-900`}
            >
              {headline}
            </h2>
            {hasBodyContent && (
              <div
                className={`${bodySizeClass("text_only", bodySize)} leading-relaxed break-words [&_p]:my-0 [&_p+p]:mt-3 [&_p:empty]:h-[1.5em] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-0 [&_li]:my-0 [&_li>p]:my-0 text-gray-700`}
                dangerouslySetInnerHTML={{ __html: safeBody }}
              />
            )}
          </motion.div>
        </div>
        {moreButton}
        {expandSheet}
      </div>
    );
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Split layout — image top half, text on white bottom half

  if (layout === "split") {
    return (
      <div
        {...pressHandlers}
        className="relative w-full h-full flex flex-col select-none"
      >
        {/* object-cover: the image fills the band edge-to-edge with no
            letterbox bars. A wider-than-the-band image is cropped to fit
            (chosen over contain, which left black bars top/bottom). The band
            height flexes toward the photo's aspect (splitBand) so the crop
            stays minimal instead of forcing every photo into a fixed ~square
            half. Text band (flex-1 below) absorbs the remainder. */}
        <div
          className="relative bg-slate-200 overflow-hidden transition-[flex-basis] duration-300"
          style={{
            flexBasis: `${(imageUrl ? splitBand : SPLIT_BAND_EMPTY) * 100}%`,
            flexGrow: 0,
            flexShrink: 0,
          }}
        >
          {/* Placeholder shown when there's no image yet, or while the photo
              is still decoding. Static for the empty state; a soft pulse
              while a real image is loading (so it reads as "loading", not
              "broken"). The image fades in over it on load. */}
          {(!imageUrl || !splitImgLoaded) && (
            <div
              className={[
                "absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300",
                imageUrl && !splitImgLoaded ? "animate-pulse" : "",
              ].join(" ")}
            >
              <ImageOutlinedIcon className="text-slate-400" style={{ fontSize: 48 }} />
            </div>
          )}
          {imageUrl && (
            <img
              ref={splitImgRef}
              src={imageUrl}
              alt=""
              className="w-full h-full object-cover transition-opacity duration-300"
              style={{ opacity: splitImgLoaded ? 1 : 0 }}
              draggable={false}
              loading="eager"
              decoding="async"
              onLoad={handleSplitImageLoad}
            />
          )}
        </div>
        <div className="flex-1 relative bg-white min-h-0">
          <div
            ref={bodyRef}
            className={`absolute inset-0 overflow-hidden flex flex-col justify-start px-6 pt-8 pb-8 min-w-0 ${textAlignClass}`}
          >
            <motion.div
              variants={fadeVariants}
              initial="initial"
              animate="enter"
              className="flex flex-col min-w-0"
            >
              {emoji && <div className="text-3xl mb-3">{emoji}</div>}
              <h2
                className={`${headlineSizeClass("split", headlineSize)} ${headlineWeightClass} ${headlineItalicClass} text-gray-900 mb-3 leading-tight break-words`}
              >
                {headline}
              </h2>
              {hasBodyContent && (
                <div
                  className={`${bodySizeClass("split", bodySize)} text-gray-700 leading-relaxed break-words [&_p]:my-0 [&_p+p]:mt-3 [&_p:empty]:h-[1.5em] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-0 [&_li]:my-0 [&_li>p]:my-0`}
                  dangerouslySetInnerHTML={{ __html: safeBody }}
                />
              )}
            </motion.div>
          </div>
          {moreButton}
        </div>
        {expandSheet}
      </div>
    );
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Full-bleed layout (default)

  // Full-bleed: gradient direction + text container position both depend
  // on `text_position`. Top → dark gradient at top, text near top.
  // Bottom → gradient at bottom, text near bottom (default). Middle →
  // softer overall gradient and vertically centered text.
  const gradientByPosition = {
    top: "linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0) 80%)",
    middle:
      "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55))",
    bottom:
      "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0) 80%)",
  };
  const textContainerPositionClass = {
    top: "absolute top-0 left-0 right-0 px-6 pt-14 text-white",
    middle:
      "absolute inset-0 flex flex-col justify-center items-stretch px-6 text-white",
    bottom: "absolute bottom-0 left-0 right-0 px-6 pb-12 text-white",
  }[textPosition];

  return (
    <div
      {...pressHandlers}
      className="relative w-full h-full overflow-hidden bg-black select-none"
    >
      <img
        src={imageUrl}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
        loading="eager"
        decoding="async"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: gradientByPosition[textPosition] }}
      />
      <motion.div
        variants={fadeVariants}
        initial="initial"
        animate="enter"
        className={`${textContainerPositionClass} ${textAlignClass}`}
      >
        {emoji && <div className="text-3xl mb-2">{emoji}</div>}
        <h2
          className={`${headlineSizeClass("full_bleed", headlineSize)} ${headlineWeightClass} ${headlineItalicClass} mb-3 leading-tight break-words`}
        >
          {headline}
        </h2>
        {hasBodyContent && (
          <div
            className={`${bodySizeClass("full_bleed", bodySize)} opacity-95 leading-relaxed break-words [&_p]:my-0 [&_p+p]:mt-3 [&_p:empty]:h-[1.5em] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-0 [&_li]:my-0 [&_li>p]:my-0`}
            dangerouslySetInnerHTML={{ __html: safeBody }}
          />
        )}
      </motion.div>
    </div>
  );
}
